
import type { MailchimpSettings } from '../types';

const STORAGE_KEY = 'tourvisio_mailchimp_settings';
const LOCAL_SUBSCRIBERS_KEY = 'tourvisio_local_subscribers';

export const getMailchimpSettings = (): MailchimpSettings => {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        if (!data) {
            return {
                apiKey: '',
                serverPrefix: '',
                audienceId: '',
                isActive: false
            };
        }
        return JSON.parse(data);
    } catch (e) {
        return { apiKey: '', serverPrefix: '', audienceId: '', isActive: false };
    }
};

export const saveMailchimpSettings = (settings: MailchimpSettings): void => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
};

export const subscribeToNewsletter = async (email: string): Promise<{ success: boolean; message: string }> => {
    const settings = getMailchimpSettings();

    // Fallback: Just save locally if not active or configured
    if (!settings.isActive || !settings.apiKey) {
        saveLocalSubscriber(email);
        return { success: true, message: 'Subscribed successfully (Local Mode)' };
    }

    const url = `https://${settings.serverPrefix}.api.mailchimp.com/3.0/lists/${settings.audienceId}/members`;

    const data = {
        email_address: email,
        status: 'subscribed'
    };

    try {
        // NOTE: Direct calls to Mailchimp from the browser often fail due to CORS.
        // In a production app, this fetch should go to a proxy server.
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `apikey ${settings.apiKey}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });

        if (response.ok) {
            return { success: true, message: 'Thank you for subscribing!' };
        } else {
            const errorData = await response.json();
            if (errorData.title === 'Member Exists') {
                return { success: true, message: 'You are already subscribed!' };
            }
            throw new Error(errorData.detail || 'Subscription failed');
        }
    } catch (error) {
        console.warn("Mailchimp API call failed (likely CORS). Saving locally as fallback.", error);
        saveLocalSubscriber(email);
        return { success: true, message: 'Thank you for subscribing!' };
    }
};

const saveLocalSubscriber = (email: string) => {
    try {
        const raw = localStorage.getItem(LOCAL_SUBSCRIBERS_KEY);
        const list: string[] = raw ? JSON.parse(raw) : [];
        if (!list.includes(email)) {
            list.push(email);
            localStorage.setItem(LOCAL_SUBSCRIBERS_KEY, JSON.stringify(list));
        }
    } catch (e) {
        console.error("Failed to save local subscriber", e);
    }
};

export const getLocalSubscribers = (): string[] => {
    try {
        const raw = localStorage.getItem(LOCAL_SUBSCRIBERS_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch (e) {
        return [];
    }
};
